import BreadCrumb from "components/Common/BreadCrumb";
import TableContainer from "components/Common/TableContainer";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Row,
} from "reactstrap";
import { createSelector } from "reselect";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import artistApi from "api/artistApi";
import FoodDrinkApi from "api/foodDrinkApi";
import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import CollapseFilter from "components/Common/CollapseFilter";
import LabelWrapper from "components/Common/LabelWrapper";
import ModalConfirm from "components/Common/ModalConfirm";
import ModalPreview from "components/Common/ModalPreview";
import TooltipCustom from "components/Common/TooltipCustom";
import { useRole } from "components/Hooks/UserHooks";
import {
  formatDateStringToKorea,
  formatNumberWithCommas,
} from "helpers/format";
import CountUp from "react-countup";
import { getFoodDrinks } from "store/foodDrink/thunk";
import { truncate } from "utils";
import CreateEditFoodDrink from "./CreateEditFoodDrink";
import ModalMultiPreview from "components/Common/ModalMultiPreview";

interface Option {
  label: string;
  value: string;
}
export interface Tag {
  id: string;
  text: string;
}

registerLocale("en", en);
registerLocale("ko", ko);

const MediaList = ({ barID }: any) => {
  const { t, i18n } = useTranslation();
  const { userPermissions } = useRole();

  const [query, setQuery]: any = useState({
    page: 1,
    limit: 30,
    keyword: "",

    sort_by: "created_at",
    order_by: "DESC",
    bar_id: barID,
  });

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState<boolean>(false);

  const [optionsSelected, setOptionsSelected] = useState<any>([]);
  const [isOpenConfirmStatusNews, setIsOpenConfirmStatusNews] =
    useState<boolean>(false);
  const [typeStatusNews, setTypeStatusNews] = useState<any>(null);

  const [itemToEdit, setItemToEdit] = useState<any>(false);

  const [keywordSearch, setKeywordSearch] = useState<string>("");

  const [listBar, setListBar] = useState<any>([]);
  const [listGroup, setListGroup] = useState<any>([]);

  const dispatch: any = useDispatch();

  const selectLayoutState = (state: any) => state.FoodDrink;

  const FoodDrinkProperties = createSelector(selectLayoutState, (state) => ({
    foodDrinks: state.foodDrinks,
    isFoodDrinkLoading: state.isFoodDrinkLoading,
    isFoodDrinkSuccess: state.isFoodDrinkSuccess,

    error: state.error,
  }));

  // Inside your component
  const { foodDrinks, isFoodDrinkLoading } = useSelector(FoodDrinkProperties);

  function loadTableData() {
    dispatch(getFoodDrinks(query));
  }

  useEffect(() => {
    // console.log("loadData");
    loadTableData();
  }, [dispatch, JSON.stringify(query)]);

  const [linkImage, setLinkImage] = useState<any>(null);
  const onCloseImageClick = () => {
    setLinkImage(null);
  };

  const searchData = () => {
    setQuery({
      ...query,
      keyword: keywordSearch || "",
      sort_by: "created_at",
      page: 1,
      time_request: +new Date(),
    });
  };

  const resetData = () => {
    setQuery(
      {
        keyword: "",
        "condition[status]": "",
        "condition[artistIds]": "",
        categoryId: "",
        start_date: "",
        end_date: "",
        sort_by: "created_at",
        time_request: +new Date(),
        page: 1,
      },
      "push"
    );
    setKeywordSearch((_prev) => "");
  };

  const handleActionStatusNews = async () => {
    if (typeStatusNews === null) {
      return;
    }

    const ids = Object.entries(optionsSelected)
      ?.reduce((arr: Option[], [key, value]: any) => {
        return [...arr, ...(value || [])];
      }, [])
      ?.map((item: Option) => item?.value);

    if (ids?.length < 1) return;

    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await FoodDrinkApi?.updateStatusFoodDrink({
        articleIds: ids,
        status: typeStatusNews,
      });

      if (response?.success === true) {
        loadTableData();

        setIsConfirmLoading((_prev) => false);
        setIsOpenConfirmStatusNews(false);
        setTypeStatusNews(null);
        setOptionsSelected((_prev: any) => ({}));

        if (response?.data?.message) {
          toast(`${response?.data?.message}`, CONFIG_OPTION_TOAST_NORMAL);
        }
      } else {
        setIsConfirmLoading((_prev) => false);
        setIsOpenConfirmStatusNews(false);
        setTypeStatusNews(null);
        toast(`${response?.message}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const onCloseConfirmActionNewsClick = () => {
    setIsOpenConfirmStatusNews(false);
    setTypeStatusNews(null);
  };

  // Column
  const columns = useMemo(
    () => [
      {
        Header: t("No."),
        thWidth: 50,
        Cell: ({ ...props }) => {
          return (
            <div>
              {foodDrinks?.meta?.total -
                ((query?.page - 1) * query?.limit + props?.row?.index)}
            </div>
          );
        },
      },
      {
        Header: t("Photo"),
        accessor: "files",
        thClass: "text-center",
        filterable: true,
        sortable: false,
        thWidth: 100,
        Cell: (cell: any) => {
          const item = cell?.row?.original;
          const imgUrl = item?.files?.[0];
          return imgUrl ? (
            <>
              <div className="w-100 text-center " style={{ minWidth: "100px" }}>
                <div
                  style={{ width: "90px" }}
                  className="m-auto cursor-pointer"
                  onClick={() => {
                    setLinkImage({
                      ...(item?.files?.length > 1 ? { urls: item?.files } : { url: imgUrl }),
                      keyword: {
                        title: item?.title,
                        keyword: item?.description,
                      },
                    });
                    document.body.className = "modal-open_img";
                  }}
                >
                  <img
                    src={imgUrl}
                    className="flex-shrink-0 rounded w-100"
                    alt="thumbnail"
                  />
                </div>
              </div>
            </>
          ) : (
            ""
          );
        },
      },
      {
        Header: t("Name"),
        accessor: "name",
        filterable: true,
        sortable: false,
        thWidth: 200,
        Cell: (cell: any) => {
          const item = cell?.row?.original?.translations?.filter(
            (i: any) => i?.lang == "ko"
          )?.[0];
          return (
            <>
              <div className="cursor-pointer" style={{ minWidth: "200px" }}>
                {truncate(item?.name, 70)}
              </div>
            </>
          );
        },
      },

      {
        Header: t("Price"),
        accessor: "price",
        filterable: true,
        sortable: false,
        thWidth: 200,
        Cell: (cell: any) => {
          const items = cell?.row?.original?.prices;
          return (
            <>
              {items?.map((i: any) => {
                return (
                  <div>
                    {formatNumberWithCommas(i?.price)} {i?.unit}
                  </div>
                );
              })}
            </>
          );
        },
      },

      {
        Header: t("Created at"),
        accessor: "created_at",
        filterable: true,
        sortable: true,
        thWidth: 150,
        thClass: "text-end",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end pe-3" style={{ minWidth: "120px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Updated at"),
        accessor: "updated_at",
        filterable: true,
        sortable: false,
        thWidth: 150,
        thClass: "text-end",
        Cell: (cell: any) => {
          const date = formatDateStringToKorea(cell?.value);
          const arrDate = String(date || "").split(" ");

          return (
            <div className="text-end" style={{ minWidth: "120px" }}>
              <span>
                <span>{arrDate?.[0] || ""}</span> <br />{" "}
                <span className="text-secondary">{arrDate?.[1] || ""}</span>{" "}
              </span>
            </div>
          );
        },
      },
      {
        Header: t("Button Action"),
        thClass: "text-center",
        thWidth: 150,
        Cell: (cell: any) => {
          const item: any = cell?.row?.original;
          return (
            <ul
              className="list-inline d-flex flex-wrap hstack gap-2 mb-0"
              style={{ justifyContent: "center" }}
            >
              <TooltipCustom
                title={t("Button Update")}
                id={`update-md-${barID}`}
              >
                <li className="list-inline-item" id={`update-md-${barID}`}>
                  <Link
                    className="btn btn-sm btn-soft-primary edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsOpen(true);
                      setItemToEdit(item);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom "></i>
                  </Link>
                </li>
              </TooltipCustom>

              <TooltipCustom
                title={t("Button Delete")}
                id={`delete-md-${barID}`}
              >
                <li className="list-inline-item" id={`delete-md-${barID}`}>
                  <Link
                    className="btn btn-sm btn-soft-danger edit-item-btn"
                    to="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setItemToDelete(item);
                    }}
                  >
                    <i className="ri-delete-bin-3-fill align-bottom"></i>
                  </Link>
                </li>
              </TooltipCustom>
            </ul>
          );
        },
      },
    ],
    [i18n?.language, foodDrinks?.total]
  );

  const handleChangePage = useCallback((page: any) => {
    setQuery({ page: page + 1 });
  }, []);

  const handleChangeSorting = useCallback((sortBy: any) => {
    setQuery((_prev: any) => {
      return { ..._prev, ...sortBy };
    });
  }, []);

  // Begin::Delete

  const handleActionDelete = async (id: any) => {
    if (!id) {
      return;
    }
    try {
      setIsConfirmLoading((_prev) => true);
      const response: any = await FoodDrinkApi?.deleteFoodDrink(id);
      if (response?.code == 200) {
        loadTableData();
        setIsConfirmLoading((_prev) => false);
        setItemToDelete(false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
      } else {
        setIsConfirmLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsConfirmLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  // End::Delete
  // console.log(barID);

  useEffect(() => {
    document.title = `${t("Menu")}| Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);
  return (
    <React.Fragment>
      <div>
        <Container fluid>
          <Row>
            <Col lg={12}>
              <div
                id="customerList"
                style={{ boxShadow: "0px 3px 4px 0px rgba(0, 0, 0, 0.03)" }}
              >
                <CardHeader className="border-0">
                  <Row className="g-4 align-items-center">
                    <div className="col-sm">
                      <div>
                        <h5 className="card-title mb-0">
                          <span className="me-2">{t("Total")}:</span>
                          <CountUp
                            start={0}
                            end={foodDrinks?.meta?.total || 0}
                            duration={1}
                            className="text-primary"
                          />
                        </h5>
                      </div>
                    </div>
                    <div className="col-sm-auto text-end">
                      <button
                        type="button"
                        className="btn btn-success d-md-block"
                        onClick={() => {
                          setIsOpen(true);
                          setItemToEdit(null);
                        }}
                      >
                        <i className="ri-add-fill align-bottom me-1"></i>{" "}
                        {t("Create")}
                      </button>
                    </div>
                  </Row>
                </CardHeader>
                <div className=" border border-dashed border-end-0 border-start-0 card-body mt-0 pb-4 px-0">
                  <CollapseFilter>
                    <Row className="g-4 align-items-center mt-md-2 mt-0">
                      <Col
                        sm={12}
                        md={3}
                        xl={3}
                        xxl={3}
                        className="mt-3 mt-md-2"
                      >
                        <LabelWrapper
                          label={t("Name")}
                          isShow={!!keywordSearch}
                        >
                          <Input
                            type="text"
                            className="form-control search"
                            placeholder={`${t("Name")}...`}
                            value={keywordSearch}
                            onChange={(e) => setKeywordSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                searchData();
                              }
                            }}
                          />
                        </LabelWrapper>
                      </Col>

                      <Col
                        sm={12}
                        md={9}
                        xl={9}
                        xxl={9}
                        className="hstack gap-1 justify-content-center justify-content-md-start mt-3 mt-md-2"
                      >
                        <div>
                          <button
                            type="button"
                            className="btn btn-primary me-1"
                            onClick={searchData}
                            disabled={isFoodDrinkLoading}
                          >
                            <i className="ri-search-line align-bottom me-1"></i>{" "}
                            {t("Button Search")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary fs-14"
                            onClick={resetData}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>{" "}
                            {t("Button Reset")}
                          </button>
                        </div>
                      </Col>
                    </Row>
                  </CollapseFilter>
                </div>
                <CardBody className="pt-3">
                  <div>
                    <TableContainer
                      className="custom-header-css"
                      divClass="table-responsive table-card"
                      tableClass="align-middle table-bordered-dashed"
                      theadClass="table-light text-muted"
                      columns={columns}
                      data={foodDrinks?.list?.length ? foodDrinks?.list : []}
                      customPageSize={query.limit}
                      customPageIndex={query.page - 1}
                      totalRecords={foodDrinks?.meta?.total}
                      customPageCount={Math.ceil(
                        Number(foodDrinks?.meta?.total) / Number(query.limit)
                      )}
                      handleChangePage={handleChangePage}
                      manualSorting={true}
                      sorting={{
                        sort_by: query.sort_by,
                        order_by: query.order_by,
                      }}
                      handleChangeSorting={handleChangeSorting}
                      isLoading={isFoodDrinkLoading}
                    />
                  </div>
                </CardBody>
              </div>
            </Col>
          </Row>
        </Container>
        {itemToDelete && (
          <ModalConfirm
            header={t("Button Delete")}
            isOpen={itemToDelete}
            isLoading={isConfirmLoading}
            onClose={() => setItemToDelete(false)}
            onConfirm={() => handleActionDelete(itemToDelete?.id)}
          />
        )}

        {isOpen && (
          <CreateEditFoodDrink
            setIsOpen={setIsOpen}
            barID={barID}
            item={itemToEdit}
            setItemToEdit={setItemToEdit}
            loadTableData={loadTableData}
            listBar={listBar}
            listGroup={listGroup}
            linkImage={linkImage}
            setLinkImage={setLinkImage}
            onCloseImageClick={onCloseImageClick}
          />
        )}

        <ModalPreview
          type={linkImage?.isVideo ? "VIDEO" : "IMAGE"}
          isOpen={!!linkImage?.url}
          url={linkImage?.url}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        <ModalMultiPreview
          isOpen={!!linkImage?.urls}
          urls={linkImage?.urls || []}
          content={linkImage?.content}
          onClose={() => onCloseImageClick()}
        />

        <ModalConfirm
          textButtonConfirm={
            typeStatusNews === "activated" ? t("Active") : t("Disable")
          }
          classButtonConfirm="btn-soft-primary"
          classIconButtonConfirm={
            typeStatusNews === "activated"
              ? "ri-checkbox-circle-line"
              : "ri-refresh-line"
          }
          header={typeStatusNews === "activated" ? t("Active") : t("Disable")}
          title={
            typeStatusNews === "activated"
              ? t("Do you want to active all the items!")
              : t("Do you want to disable all the items!")
          }
          content={""}
          isShowIcon={false}
          isOpen={isOpenConfirmStatusNews}
          isLoading={isConfirmLoading}
          onClose={onCloseConfirmActionNewsClick}
          onConfirm={handleActionStatusNews}
        />

        {/* <ModalConfirm
          textButtonConfirm={t('Button Update')}
          classButtonConfirm='btn-primary'
          classIconButtonConfirm=''
          header={(String(itemUpdate?.status) === ENUM_STATUS_FEED.ACTIVATED) ? t('Confirm to ON') : t('Confirm to OFF')}
          title={t('Are you sure you want to update this?')}
          content={t('After confirming the update action, the data will be updated. Do you want to proceed with the update action.')}
          isShowIcon={false}
          isOpen={!!itemUpdate}
          isLoading={isLoadingUpdate}
          onClose={() => setItemUpdate((prev: any) => null)}
          onConfirm={handleOnConfirmUpdate}
        /> */}
      </div>
    </React.Fragment>
  );
};

export default MediaList;
